import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import api from "../../api";
import { LoadingState } from "../../components/ui/LoadingState";
import { ErrorState } from "../../components/ui/ErrorState";
import EmptyState from "../../components/common/EmptyState";
import { ACTIVITY_SOURCE_META, timeAgo } from "./projectWorkspaceUtils";
import { AuthContext } from "../../context/AuthContext";
import "./ProjectWorkspace.css";

// Simple activity feed -- GET /projects/:id/activity, newest-first (server
// order preserved, no client-side re-sorting). Each item's `source` maps to
// an icon/label via ACTIVITY_SOURCE_META (projectWorkspaceUtils.js); items
// carrying a taskId are clickable through to that task's detail page, the
// SAME 'tasks/:taskId' route every role already has (routes/
// DynamicRoutes.jsx's shared entry) -- role-aware, never hardcoded to
// /admin/tasks/... (this tab is reached through the same shared workspace
// component from /developer/projects/:projectId too).
export default function ProjectWorkspaceActivityTab({ projectId }) {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext) || {};
  const basePath = `/${user?.role || "admin"}`;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    setError("");
    api
      .get(`/projects/${projectId}/activity`)
      .then((res) => setItems(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch((err) => setError(err.response?.data?.message || "Failed to load activity"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <LoadingState message="Loading activity…" />;
  if (error) return <ErrorState message={error} action={<Button onClick={load}>Retry</Button>} />;
  if (items.length === 0) {
    return (
      <EmptyState
        title="No activity yet"
        message="Status changes, assignments, remarks and phase/milestone updates will show up here."
      />
    );
  }

  return (
    <div className="pw-activity-list">
      {items.map((item, idx) => {
        const meta = ACTIVITY_SOURCE_META[item.source] || { icon: "fa-circle-dot", label: item.source || "Activity" };
        const clickable = !!item.taskId;
        const goToTask = () => navigate(`${basePath}/tasks/${item.taskId}`);
        return (
          <div
            className={`pw-activity-item${clickable ? " pw-activity-item-clickable" : ""}`}
            key={`${item.source}-${item.at}-${idx}`}
            role={clickable ? "button" : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? goToTask : undefined}
            onKeyDown={
              clickable
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      goToTask();
                    }
                  }
                : undefined
            }
          >
            <div className="pw-activity-icon">
              <i className={`fa-solid ${meta.icon}`} aria-hidden="true"></i>
            </div>
            <div className="pw-activity-body">
              <div className="pw-activity-summary">
                {item.summary}
                {item.taskId && <span className="pw-chip pw-chip-secondary ms-2">Task #{item.taskId}</span>}
              </div>
              <div className="pw-activity-time">
                <span className="pw-chip pw-chip-secondary">{meta.label}</span>{" "}
                {timeAgo(item.at)}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
